import {
    Add as AddIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    LocalShipping as ShippingIcon,
    ToggleOff as ToggleOffIcon,
    ToggleOn as ToggleOnIcon,
} from '@mui/icons-material';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    MenuItem,
    Paper,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tabs,
    TextField,
    Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import shippingRatesService from '../services/shippingRatesService';

const ShippingRates = () => {
  const [rates, setRates] = useState([]);
  const [zones, setZones] = useState([]);
  const [carriers, setCarriers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [selectedCarrier, setSelectedCarrier] = useState('colissimo');
  const [selectedZone, setSelectedZone] = useState('france');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRate, setEditingRate] = useState(null);
  const [formData, setFormData] = useState({
    carrier: 'colissimo',
    serviceCode: '',
    serviceName: '',
    serviceDescription: '',
    zone: 'france',
    weightMin: '',
    weightMax: '',
    priceHt: '',
    priceTtc: '',
    isActive: true,
    sortOrder: 0,
  });

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    loadRates();
  }, [selectedCarrier, selectedZone]);

  const loadData = async () => {
    try {
      const [zonesRes, carriersRes] = await Promise.all([
        shippingRatesService.getZones(),
        shippingRatesService.getCarriers(),
      ]);
      setZones(zonesRes.data.data);
      setCarriers(carriersRes.data.data);
      await loadRates();
    } catch (err) {
      setError('Erreur lors du chargement des données');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadRates = async () => {
    try {
      const response = await shippingRatesService.getAll({
        carrier: selectedCarrier,
        zone: selectedZone,
      });
      setRates(response.data.data);
    } catch (err) {
      setError('Erreur lors du chargement des tarifs');
      console.error(err);
    }
  };

  const handleOpenDialog = (rate = null) => {
    if (rate) {
      setEditingRate(rate);
      setFormData({
        carrier: rate.carrier,
        serviceCode: rate.serviceCode,
        serviceName: rate.serviceName,
        serviceDescription: rate.serviceDescription || '',
        zone: rate.zone,
        weightMin: rate.weightMin,
        weightMax: rate.weightMax,
        priceHt: rate.priceHt,
        priceTtc: rate.priceTtc,
        isActive: rate.isActive,
        sortOrder: rate.sortOrder || 0,
      });
    } else {
      setEditingRate(null);
      setFormData({
        carrier: selectedCarrier,
        serviceCode: '',
        serviceName: '',
        serviceDescription: '',
        zone: selectedZone,
        weightMin: '',
        weightMax: '',
        priceHt: '',
        priceTtc: '',
        isActive: true,
        sortOrder: 0,
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingRate(null);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;
    
    setFormData((prev) => {
      const updated = { ...prev, [name]: newValue };
      
      // Calcul automatique du TTC depuis le HT
      if (name === 'priceHt' && value) {
        const ht = parseFloat(value);
        updated.priceTtc = (ht * 1.2).toFixed(2);
      }
      
      // Calcul automatique du HT depuis le TTC
      if (name === 'priceTtc' && value) {
        const ttc = parseFloat(value);
        updated.priceHt = (ttc / 1.2).toFixed(2);
      }
      
      return updated;
    });
  };

  const handleSubmit = async () => {
    try {
      if (editingRate) {
        await shippingRatesService.update(editingRate.id, formData);
        setSuccess('Tarif mis à jour avec succès');
      } else {
        await shippingRatesService.create(formData);
        setSuccess('Tarif créé avec succès');
      }
      handleCloseDialog();
      await loadRates();
    } catch (err) {
      setError(editingRate ? 'Erreur lors de la mise à jour' : 'Erreur lors de la création');
      console.error(err);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce tarif ?')) {
      return;
    }
    try {
      await shippingRatesService.delete(id);
      setSuccess('Tarif supprimé avec succès');
      await loadRates();
    } catch (err) {
      setError('Erreur lors de la suppression');
      console.error(err);
    }
  };

  const handleToggleActive = async (id) => {
    try {
      await shippingRatesService.toggleActive(id);
      setSuccess('Statut modifié avec succès');
      await loadRates();
    } catch (err) {
      setError('Erreur lors du changement de statut');
      console.error(err);
    }
  };

  const getZoneLabel = (zoneValue) => {
    const zone = zones.find((z) => z.value === zoneValue);
    return zone ? zone.label : zoneValue;
  };

  const getCarrierLabel = (carrierValue) => {
    const carrier = carriers.find((c) => c.value === carrierValue);
    return carrier ? carrier.label : carrierValue;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ShippingIcon sx={{ fontSize: 32 }} />
          <Typography variant="h4">Tarifs de Livraison</Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
          Ajouter un tarif
        </Button>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Paper sx={{ mb: 3 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={selectedCarrier} onChange={(e, val) => setSelectedCarrier(val)}>
            {carriers.map((carrier) => (
              <Tab key={carrier.value} label={carrier.label} value={carrier.value} />
            ))}
          </Tabs>
        </Box>
        <Box sx={{ p: 2 }}>
          <Tabs value={selectedZone} onChange={(e, val) => setSelectedZone(val)}>
            {zones.map((zone) => (
              <Tab key={zone.value} label={zone.label} value={zone.value} />
            ))}
          </Tabs>
        </Box>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Service</TableCell>
              <TableCell>Code</TableCell>
              <TableCell>Poids Min (kg)</TableCell>
              <TableCell>Poids Max (kg)</TableCell>
              <TableCell align="right">Prix HT</TableCell>
              <TableCell align="right">Prix TTC</TableCell>
              <TableCell>Statut</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rates.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography variant="body2" color="textSecondary">
                    Aucun tarif trouvé pour cette sélection
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              rates.map((rate) => (
                <TableRow key={rate.id}>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {rate.serviceName}
                    </Typography>
                    {rate.serviceDescription && (
                      <Typography variant="caption" color="textSecondary">
                        {rate.serviceDescription}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip label={rate.serviceCode} size="small" />
                  </TableCell>
                  <TableCell>{rate.weightMin} kg</TableCell>
                  <TableCell>{rate.weightMax} kg</TableCell>
                  <TableCell align="right">
                    <Typography fontWeight="bold">{Number(rate.priceHt).toFixed(2)} €</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography fontWeight="bold" color="primary">
                      {Number(rate.priceTtc).toFixed(2)} €
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={rate.isActive ? 'Actif' : 'Inactif'}
                      color={rate.isActive ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={() => handleToggleActive(rate.id)}
                      title={rate.isActive ? 'Désactiver' : 'Activer'}
                    >
                      {rate.isActive ? <ToggleOnIcon color="success" /> : <ToggleOffIcon />}
                    </IconButton>
                    <IconButton size="small" onClick={() => handleOpenDialog(rate)} title="Modifier">
                      <EditIcon />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDelete(rate.id)} title="Supprimer">
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{editingRate ? 'Modifier le tarif' : 'Nouveau tarif'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={6}>
              <TextField
                select
                fullWidth
                label="Transporteur"
                name="carrier"
                value={formData.carrier}
                onChange={handleInputChange}
              >
                {carriers.map((carrier) => (
                  <MenuItem key={carrier.value} value={carrier.value}>
                    {carrier.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField
                select
                fullWidth
                label="Zone"
                name="zone"
                value={formData.zone}
                onChange={handleInputChange}
              >
                {zones.map((zone) => (
                  <MenuItem key={zone.value} value={zone.value}>
                    {zone.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Code Service"
                name="serviceCode"
                value={formData.serviceCode}
                onChange={handleInputChange}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nom du Service"
                name="serviceName"
                value={formData.serviceName}
                onChange={handleInputChange}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Description"
                name="serviceDescription"
                value={formData.serviceDescription}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                type="number"
                label="Poids Min (kg)"
                name="weightMin"
                value={formData.weightMin}
                onChange={handleInputChange}
                inputProps={{ step: 0.001, min: 0 }}
                required
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                type="number"
                label="Poids Max (kg)"
                name="weightMax"
                value={formData.weightMax}
                onChange={handleInputChange}
                inputProps={{ step: 0.001, min: 0 }}
                required
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                type="number"
                label="Prix HT (€)"
                name="priceHt"
                value={formData.priceHt}
                onChange={handleInputChange}
                inputProps={{ step: 0.01, min: 0 }}
                required
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                type="number"
                label="Prix TTC (€)"
                name="priceTtc"
                value={formData.priceTtc}
                onChange={handleInputChange}
                inputProps={{ step: 0.01, min: 0 }}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                type="number"
                label="Ordre de tri"
                name="sortOrder"
                value={formData.sortOrder}
                onChange={handleInputChange}
                inputProps={{ step: 1 }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Annuler</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingRate ? 'Mettre à jour' : 'Créer'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ShippingRates;

